<template>
  <div class="credit-line-page">
    <v-container fluid class="px-15 pb-10 pt-4">

      <v-row class="ma-0 mt-5 mb-8" align="start">
        <v-col cols="12" md="7" class="pa-0">
          <div class="d-flex align-center flex-wrap">
            <div class="page-title">{{ creditLine.client }}</div>
            <v-chip
              color="#DFF5EE"
              text-color="#009762"
              class="ml-4 status-chip"
            >
              {{ creditLine.status }}
            </v-chip>
          </div>
          <div class="page-subtitle mt-3">
            {{ creditLine.id }} · {{ creditLine.rfc }}
          </div>
        </v-col>

        <v-col cols="12" md="5" class="pa-0 d-flex justify-end">
          <v-btn
            color="primary"
            rounded
            class="white--text detail-action-btn"
            @click="dialogPayment = true"
          >
            <span class="mr-3 money-icon">$</span>
            Realizar Pago
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="ma-0 mb-2">
        <v-col
          v-for="(metric, index) in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          lg="3"
          :class="[
            'pl-0 pb-4',
            index === metrics.length - 1 ? 'pr-0' : 'pr-4'
          ]"
        >
          <v-card outlined class="metric-card">
            <div>
              <div class="metric-label">{{ metric.label }}</div>
              <div class="metric-value">{{ metric.value }}</div>
              <div v-if="metric.helper" class="metric-helper">
                {{ metric.helper }}
              </div>
            </div>

            <div class="metric-icon">
              <v-icon :color="metric.color" size="34">
                {{ metric.icon }}
              </v-icon>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="detail-tabs-card">
        <v-tabs
          v-model="activeTab"
          background-color="white"
          color="primary"
          slider-color="primary"
          class="detail-tabs"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.value"
            class="detail-tab"
          >
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-tabs-items v-model="activeTab" class="tabs-content">
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <div class="section-title">Información del Cliente</div>
                <div
                  v-for="item in clientInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="section-title">Condiciones Financieras</div>
                <div
                  v-for="item in financialInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-box">
                  <div class="info-box__title">Tipo de Cálculo</div>
                  <div class="info-box__text">Interés mensual sobre saldo insoluto</div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-box">
                  <div class="info-box__title">Orden de Aplicación de Pagos</div>
                  <div class="info-box__text">Interés primero, luego capital</div>
                </div>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-header-row">
              <div class="content-title">{{ currentCycleTitle }}</div>
              <v-btn
                outlined
                rounded
                color="primary"
                class="detail-action-btn"
              >
                <v-icon left>mdi-download-outline</v-icon>
                Descargar Estado de Cuenta
              </v-btn>
            </div>

            <v-row class="mb-0">
              <v-col
                v-for="item in currentCycleSummary"
                :key="item.label"
                cols="12"
                md="4"
              >
                <div class="cycle-summary-card">
                  <div>{{ item.label }}</div>
                  <strong>{{ item.value }}</strong>
                </div>
              </v-col>
            </v-row>

            <div class="cycle-panel">
              <div class="panel-title">Intereses del Ciclo</div>
              <div
                v-for="item in cycleInterestRows"
                :key="item.label"
                class="summary-row"
              >
                <span>{{ item.label }}</span>
                <strong :class="item.valueClass">{{ item.value }}</strong>
              </div>
            </div>

            <div class="cycle-panel">
              <div class="panel-title">Pagos Recibidos</div>
              <div
                v-for="item in receivedPaymentRows"
                :key="item.label"
                class="summary-row"
              >
                <span>{{ item.label }}</span>
                <strong :class="item.valueClass">{{ item.value }}</strong>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <v-data-table
              :headers="cycleHistoryHeaders"
              :items="cycleHistory"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.status="{ item }">
                <v-chip color="#EEF2F6" text-color="#60708a" class="table-chip">
                  {{ item.status }}
                </v-chip>
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item>
            <v-data-table
              :headers="paymentHistoryHeaders"
              :items="paymentHistory"
              hide-default-footer
              disable-sort
              class="detail-table"
            />
          </v-tab-item>

          <v-tab-item>
            <v-data-table
              :headers="documentHeaders"
              :items="documents"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.actions>
                <v-btn icon color="primary">
                  <v-icon>mdi-download-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <v-dialog v-model="dialogPayment" max-width="860" persistent>
        <v-card class="payment-dialog">
          <v-card-title class="payment-dialog__title">
            <div>
              <div class="payment-title">Realizar Pago</div>
              <div class="payment-subtitle">
                {{ creditLine.id }} · {{ creditLine.client }}
              </div>
            </div>

            <v-btn icon color="#60708a" @click="dialogPayment = false">
              <v-icon size="30">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="payment-dialog__content">
            <div class="current-state">
              <div class="state-title">Monto sugerido</div>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="state-label">Interés pendiente:</div>
                  <div class="state-value pending">{{ paymentState.interest }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="state-label">Capital pendiente:</div>
                  <div class="state-value">{{ paymentState.capital }}</div>
                </v-col>
              </v-row>
            </div>

            <div class="bank-card">
              <div class="bank-card__header">
                <div>
                  <div class="bank-title">Información bancaria Fresher</div>
                  <div class="bank-helper">Realiza tu transferencia usando estos datos.</div>
                </div>
                <v-icon color="primary" size="32">mdi-bank-outline</v-icon>
              </div>

              <div
                v-for="item in bankInfo"
                :key="item.label"
                class="bank-row"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <v-btn icon small color="primary" @click="copyToClipboard(item.value)">
                  <v-icon size="18">mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <label class="field-label">Monto que pagarás</label>
                <v-text-field
                  v-model="paymentNotice.amount"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Referencia bancaria</label>
                <v-text-field
                  v-model="paymentNotice.reference"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12">
                <label class="field-label">Comprobante de pago</label>
                <button type="button" class="receipt-upload" @click="$refs.receiptFile.click()">
                  <v-icon color="primary" size="26">mdi-cloud-upload-outline</v-icon>
                  <span>{{ receiptFileName || "Subir comprobante PDF, PNG o JPG" }}</span>
                </button>
                <input
                  ref="receiptFile"
                  class="hidden-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  @change="handleReceiptChange"
                />
              </v-col>

              <v-col cols="12">
                <label class="field-label">Comentarios</label>
                <v-textarea
                  v-model="paymentNotice.comments"
                  placeholder="Notas adicionales sobre tu pago..."
                  outlined
                  hide-details
                  rows="3"
                  no-resize
                  class="dialog-input"
                />
              </v-col>
            </v-row>

            <div class="payment-note">
              <v-icon color="primary" size="22">mdi-information-outline</v-icon>
              <span>
                Este aviso no aplica el pago automáticamente. El equipo de Fresher
                validará la transferencia y actualizará tu línea de crédito.
              </span>
            </div>
          </v-card-text>

          <v-divider class="mx-8" />

          <v-card-actions class="payment-actions">
            <v-spacer />
            <v-btn outlined rounded color="primary" class="payment-btn" @click="dialogPayment = false">
              Cancelar
            </v-btn>
            <v-btn color="primary" rounded class="white--text payment-btn" @click="confirmPaymentNotice">
              Enviar aviso de pago
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "ClientCreditLine",
  data() {
    return {
      activeTab: 0,
      dialogPayment: false,
      receiptFileName: "",
      creditLine: {
        client: "Empresa Demo SA de CV",
        id: "4d4d658f-2f79-4f1c-8903-37c7e38e4e12",
        rfc: "XAXX010101000",
        status: "Activa"
      },
      tabs: [
        { label: "Información General", value: "general" },
        { label: "Ciclo Actual", value: "currentCycle" },
        { label: "Historial de Ciclos", value: "cycleHistory" },
        { label: "Historial de Pagos", value: "paymentHistory" },
        { label: "Documentos", value: "documents" }
      ],
      metrics: [
        { label: "Monto Aprobado", value: "USD 100,000", icon: "mdi-trending-up", color: "#0075B8" },
        { label: "Saldo Insoluto", value: "USD 100,000", helper: "Capital pendiente", icon: "mdi-currency-usd", color: "#E65100" },
        { label: "Interés del Periodo", value: "USD 2,500", icon: "mdi-trending-up", color: "#009762" },
        { label: "Próxima Fecha de Pago", value: "05 jun 2026", helper: "3 días vencidos", icon: "mdi-calendar-month-outline", color: "#0288D1" }
      ],
      clientInfo: [
        { label: "Cliente:", value: "Empresa Demo SA de CV" },
        { label: "RFC:", value: "XAXX010101000" },
        { label: "Email:", value: "mguererro@fresherpay.com" }
      ],
      financialInfo: [
        { label: "Monto Aprobado:", value: "USD 100,000" },
        { label: "Desembolsado:", value: "USD 100,000" },
        { label: "Tasa Mensual:", value: "2.50%" },
        { label: "Fecha de Desembolso:", value: "05 may 2026" }
      ],
      currentCycleSummary: [
        { label: "Periodo", value: "05 may 2026 - 05 jun 2026" },
        { label: "Capital Inicial", value: "USD 100,000" },
        { label: "Saldo Restante", value: "USD 100,000" }
      ],
      cycleInterestRows: [
        { label: "Interés calculado:", value: "USD 2,500" },
        { label: "Interés pagado:", value: "USD 0" },
        { label: "Interés pendiente:", value: "USD 2,500", valueClass: "warning-text" }
      ],
      receivedPaymentRows: [
        { label: "Pagos recibidos:", value: "USD 0" },
        { label: "Capital abonado:", value: "USD 0" },
        { label: "Saldo pendiente:", value: "USD 100,000", valueClass: "warning-text" }
      ],
      cycleHistoryHeaders: [
        { text: "Periodo", value: "period" },
        { text: "Capital inicial", value: "initialCapital" },
        { text: "Interés", value: "interest" },
        { text: "Pagado", value: "paid" },
        { text: "Saldo final", value: "finalBalance" },
        { text: "Estatus", value: "status" }
      ],
      cycleHistory: [
        {
          period: "May 2026",
          initialCapital: "USD 100,000",
          interest: "USD 2,500",
          paid: "USD 0",
          finalBalance: "USD 100,000",
          status: "Abierto"
        },
        {
          period: "Abr 2026",
          initialCapital: "USD 100,000",
          interest: "USD 2,500",
          paid: "USD 2,500",
          finalBalance: "USD 100,000",
          status: "Cerrado"
        }
      ],
      paymentHistoryHeaders: [
        { text: "Fecha", value: "date" },
        { text: "Monto", value: "amount" },
        { text: "A interés", value: "interest" },
        { text: "A capital", value: "capital" },
        { text: "Saldo después", value: "balanceAfter" },
        { text: "Método", value: "method" },
        { text: "Referencia", value: "reference" }
      ],
      paymentHistory: [
        {
          date: "05 may 2026",
          amount: "USD 2,500",
          interest: "USD 2,500",
          capital: "USD 0",
          balanceAfter: "USD 100,000",
          method: "Transferencia",
          reference: "FP-CL-202605"
        }
      ],
      documentHeaders: [
        { text: "Documento", value: "name" },
        { text: "Periodo", value: "period" },
        { text: "Fecha", value: "date" },
        { text: "Acciones", value: "actions", align: "center" }
      ],
      documents: [
        { name: "Contrato de línea de crédito", period: "-", date: "05 may 2026" },
        { name: "Estado de cuenta May 2026", period: "May 2026", date: "05 jun 2026" },
        { name: "Comprobante de pago", period: "Abr 2026", date: "05 may 2026" }
      ],
      paymentState: {
        interest: "USD 2,500",
        capital: "USD 100,000"
      },
      bankInfo: [
        { label: "Banco", value: "BBVA México" },
        { label: "Beneficiario", value: "Fresher Pay S.A. de C.V." },
        { label: "CLABE", value: "012180001234567890" },
        { label: "Cuenta", value: "0012345678" },
        { label: "Referencia", value: "FP-CL-4D4D-202606" },
        { label: "Concepto", value: "Pago línea de crédito Empresa Demo" }
      ],
      paymentNotice: {
        amount: "USD 2,500",
        reference: "FP-CL-4D4D-202606",
        comments: ""
      }
    };
  },
  computed: {
    currentCycleTitle() {
      return "Ciclo Actual: Jun 2026";
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    copyToClipboard(value) {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(value);
      }
    },
    handleReceiptChange(event) {
      const file = event.target.files[0];
      this.receiptFileName = file ? file.name : "";
    },
    confirmPaymentNotice() {
      this.dialogPayment = false;
      if (this.mixSuccess) {
        this.mixSuccess("Aviso de pago enviado para validación.");
      }
    }
  }
};
</script>

<style scoped>
.credit-line-page {
  min-height: 100vh;
  background: #f7f8fb;
}

.page-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 34px;
  line-height: 1.15;
}

.page-subtitle,
.metric-label,
.metric-helper,
.section-title,
.info-row span,
.info-box__text,
.summary-row span {
  color: #60708a;
}

.page-subtitle {
  font-family: 'montserrat-medium';
  font-size: 18px;
}

.status-chip,
.table-chip {
  font-family: 'montserrat-bold';
}

.detail-action-btn {
  min-height: 46px !important;
  padding: 0 22px !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.money-icon {
  font-family: 'montserrat-bold';
  font-size: 20px;
}

.metric-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 170px;
  padding: 36px 38px;
  border-radius: 12px;
  height: 100%;
}

.metric-label {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.metric-value {
  margin-top: 18px;
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 34px;
  line-height: 1;
}

.metric-helper {
  margin-top: 14px;
  font-family: 'montserrat-medium';
  font-size: 15px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  background: #f0f3f7;
}

.detail-tabs-card {
  overflow: hidden;
  border-radius: 12px;
}

.detail-tabs ::v-deep .v-slide-group__content {
  padding-left: 24px;
}

.detail-tab {
  min-width: auto;
  padding: 0 26px;
  justify-content: flex-start;
  color: #60708a !important;
  font-family: 'montserrat-bold';
  font-size: 17px;
  letter-spacing: 0;
  text-transform: none;
}

.detail-tab.v-tab--active {
  color: var(--v-primary-base) !important;
}

.tabs-content {
  padding: 34px 40px 40px;
}

.section-title {
  margin-bottom: 24px;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid #dce3ec;
  font-family: 'montserrat-medium';
  font-size: 17px;
}

.info-row strong {
  color: #1f2330;
  font-family: 'montserrat-bold';
  text-align: right;
}

.info-box,
.cycle-summary-card,
.cycle-panel {
  border: 1px solid #dce3ec;
  border-radius: 10px;
  background: #ffffff;
}

.info-box {
  padding: 24px;
}

.info-box__title,
.panel-title,
.content-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
}

.info-box__title,
.panel-title {
  margin-bottom: 18px;
  font-size: 18px;
}

.info-box__text {
  font-family: 'montserrat-medium';
  font-size: 17px;
}

.tab-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.content-title {
  font-size: 22px;
}

.cycle-summary-card {
  padding: 24px;
  font-family: 'montserrat-bold';
}

.cycle-summary-card div {
  color: #60708a;
  font-size: 15px;
}

.cycle-summary-card strong {
  display: block;
  margin-top: 8px;
  color: #1f2330;
  font-size: 22px;
}

.cycle-panel {
  margin-top: 22px;
  padding: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #eef2f6;
  font-family: 'montserrat-medium';
  font-size: 16px;
}

.summary-row:last-child {
  border-bottom: 0;
}

.summary-row strong {
  color: #1f2330;
  font-family: 'montserrat-bold';
}

.warning-text {
  color: #E65100 !important;
}

.detail-table {
  font-family: 'montserrat-medium';
}

.detail-table ::v-deep th {
  color: #60708a !important;
  font-family: 'montserrat-bold';
  font-size: 14px !important;
}

.detail-table ::v-deep td {
  color: #1f2330;
  font-size: 14px !important;
}

.payment-dialog {
  border-radius: 18px;
}

.payment-dialog__title {
  display: flex;
  justify-content: space-between;
  padding: 28px 32px 10px;
}

.payment-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 26px;
}

.payment-subtitle,
.bank-helper {
  margin-top: 6px;
  color: #60708a;
  font-family: 'montserrat-medium';
  font-size: 15px;
}

.payment-dialog__content {
  padding: 24px 32px;
}

.current-state,
.bank-card,
.payment-note {
  border-radius: 12px;
  background: #f8fafc;
}

.current-state {
  margin-bottom: 20px;
  padding: 22px;
}

.state-title,
.bank-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.state-label {
  margin-top: 18px;
  color: #60708a;
  font-family: 'montserrat-medium';
}

.state-value {
  margin-top: 6px;
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 24px;
}

.state-value.pending {
  color: #E65100;
}

.bank-card {
  margin-bottom: 22px;
  padding: 22px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
}

.bank-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.bank-row {
  display: grid;
  grid-template-columns: 150px 1fr 34px;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #dce3ec;
  font-family: 'montserrat-medium';
}

.bank-row:last-child {
  border-bottom: 0;
}

.bank-row span {
  color: #60708a;
}

.bank-row strong {
  color: #1f2330;
  font-family: 'montserrat-bold';
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #60708a;
  font-family: 'montserrat-bold';
  font-size: 14px;
}

.dialog-input ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

.receipt-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  border: 1px dashed #9cc9f2;
  border-radius: 12px;
  background: #f8fbff;
  color: #60708a;
  cursor: pointer;
  font-family: 'montserrat-bold';
}

.hidden-input {
  display: none;
}

.payment-note {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  padding: 14px;
  color: #60708a;
  font-family: 'montserrat-medium';
}

.payment-actions {
  padding: 20px 32px 28px;
}

.payment-btn {
  min-height: 44px !important;
  padding: 0 24px !important;
  font-family: 'montserrat-bold';
  text-transform: none;
}

@media (max-width: 960px) {
  .credit-line-page .container {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .page-title {
    font-size: 28px;
  }

  .tab-header-row,
  .bank-card__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .bank-row {
    grid-template-columns: 1fr;
  }
}
</style>
